import { Link, useLocation } from 'react-router-dom'

import Lockup from '../components/Lockup.jsx'
import TierBadge from '../components/TierBadge.jsx'
import PortalHome from '../portal/PortalHome.jsx'
import { childSections, commands, landing, topLevelSections } from '../content.js'
import { SIGNED_IN, signInHref, useSession } from '../session.js'
import { useDocumentTitle } from '../useDocumentTitle.js'

// `/` — two faces, one URL. P6-83.
//
// ═══════════════════════════════════════════════════════════════════════════
// 🔴 THE INVERSION, AND IT IS A RULING RATHER THAN A PREFERENCE
// ═══════════════════════════════════════════════════════════════════════════
//
// MP1, from Jordan, 2026-08-20: *"We need to treat this 'outside non-logged in'
// perspective as the one that is LESS likely to be using this site. The most
// frequent users we should expect to be server members — it should feel like an
// integration with the server."* Signed-in members land on the portal home; the
// logged-out face is the clean, small public site it has always been.
//
// This file is that switch and nothing else. Both branches own their own
// `useDocumentTitle`, deliberately: a title effect here would run AFTER the
// child's (React runs a child's effects before its parent's) and overwrite it,
// so every signed-in member would get the public page's title in their tab and
// in their screen reader's announcement after navigation.
//
// ⚠️ `loading` RENDERS THE PUBLIC FACE, WHICH IS THE FAIL-CLOSED DIRECTION. It
// is the same rule `session.js` states for the shell: any answer that is not a
// confirmed session shows LESS. The portal holds nothing secret — everything
// personal on it arrives from a Function that establishes its own caller — so
// the cost of the wrong guess here is one flash of the public page, and the cost
// of guessing the other way would be a member-shaped shell around no data.

export default function Landing() {
  // 🔴 THE THIRD COMPONENT TO CALL `useSession`, AND THE COST DID NOT MOVE.
  // session.js's note asks for a rethink "if a page ever needs it a third time"
  // — the number that matters there is REQUESTS PER PAGE LOAD, and it is still
  // two: the shell plus whichever page component asks. `/` is Layout + this;
  // `/trips` is Layout + Trips. No page makes three. If one ever does, that is
  // the moment to reach for a provider.
  const session = useSession()

  if (session.status === SIGNED_IN) return <PortalHome userId={session.userId} />
  return <PublicLanding />
}

// ═══════════════════════════════════════════════════════════════════════════
// THE PUBLIC INDEX IS DERIVED FROM THE REGISTRY, NEVER TYPED — P6-183
// ═══════════════════════════════════════════════════════════════════════════
//
// This is the same call src/routes/DocsIndex.jsx made for /docs, for the same
// reason: a hand-written list of the public pages would be a second registry,
// and the first page it disagreed with would be a link to a 404 — or worse, a
// page nobody could find because nothing linked to it.
//
// The filter is the one the shell's nav already uses. Tier 1 is what "public"
// means (content-src/sections.json: "public, indexed. No member names, no
// plans, no history, no scores"), and a tier-3 section is never advertised to
// somebody who cannot open it — so /trips appears here for nobody, because this
// component only ever renders to a reader with no session.
//
// `landing` drops out because it is the page you are standing on.
const SELF = 'landing'

function publicPages() {
  return topLevelSections.filter((section) => section.tier === 1 && section.id !== SELF)
}

/**
 * A count for one index row, or nothing at all.
 *
 * 🔴 EVERY NUMBER ON THIS PAGE IS DERIVED, AND THE DESIGN IS THE PROOF THAT IT
 * HAS TO BE. The canvas was drawn on 2026-09-03 with `8 pages` and
 * `217 · 15 groups` on these two rows. Measured on 2026-09-04, one day later:
 * five pages and 182 commands. Both literals were already false — P6-165 and
 * P6-166 took two feature pages down the morning after the design session, and
 * the command total moves whenever a cog does. A hardcoded count here would
 * have shipped a wrong number on the first screen of the public site, and
 * nothing in the suite could have known.
 *
 * ⚠️ The second half of the canvas's command label is dropped rather than
 * derived, and that is a correction rather than a shortcut: `15 groups` uses a
 * word this repo spends effort on meaning something else by. A slash GROUP is a
 * container that cannot be typed, and generate-content.mjs filters every one of
 * them OUT of the published catalog ("the published count and the published
 * list can never disagree"). Rendering a group count beside a command count
 * would put two numbers on one row that were counted under opposite rules.
 *
 * ⚠️ A zero renders NOTHING, not "0 pages". That is the portal handoff's rule
 * one surface over — "until a source exists for a pill's count, render the pill
 * with no count rather than a zero" — and it is what stops this row asserting
 * an empty site if a registry ever comes up short. /releases has no count at
 * all today, by the same rule and the design's own choice: nothing is published
 * until 2.0 announces, and `0 releases` beside a link is worse than silence.
 */
function indexCount(section) {
  if (section.id === 'docs') return countLabel(childSections('docs').length, 'page')
  // The number the destination page publishes as its own headline total, from
  // the same field — so the two cannot disagree about how many commands exist.
  if (section.id === 'commands') return countLabel(commands.counts?.commands, 'command')
  return null
}

/**
 * `12 pages`, `1 page`, or nothing at all.
 *
 * 🔴 EXPORTED FOR A TEST, AND THE REASON IS THE WHOLE POINT OF THE RULE IT
 * CARRIES. The zero branch cannot be reached by rendering this page today —
 * /docs has five children and the catalog has 182 commands — so a mutation that
 * deleted the guard survived the whole suite. An unreachable rule with no
 * assertion on it is a comment, and this one is not decorative: P6-165 and
 * P6-166 took two feature pages down in a single morning on Jordan's ruling
 * about shelved features, so "the docs count is zero" is a mechanism this repo
 * has already run, not a hypothetical.
 *
 * Nothing beats a zero here. `0 pages` next to a live link reads as a broken
 * page rather than an empty one, which is the portal handoff's rule for a pill
 * with no source behind it.
 */
export function countLabel(n, noun) {
  if (!Number.isInteger(n) || n <= 0) return null
  return `${n} ${noun}${n === 1 ? '' : 's'}`
}

// The signed-out face, built from docs/design-briefs/portal/Signed-out landing.dc.html.
//
// Top to bottom: the lockup, the lede, ONE sign-in call, then the public index
// — Documentation, Commands, Release notes. On a wide screen the prose and the
// call sit left of the index rather than above it; the width that happens at is
// a layout decision and lives in app.css, not in a number repeated here.
//
// 🔴 THE THREE DOORS ARE GONE AND TWO OF THEM WENT ON THEIR OWN MERITS.
// "I'm in the server" was a signpost to the top bar, and the page now carries
// the control it was pointing at. "I was sent an event link" described a tier-2
// surface — random slug, noindex, expires — and generate-content.mjs REFUSES a
// tier-2 section outright, so no such page can exist here: the landing page was
// advertising a capability the build forbids. content-src/landing.json carries
// the long version of both.
//
// ⚠️ TWO CONTROLS NOW SAY "SIGN IN WITH DISCORD" ON THIS PAGE — this one and the
// shell's. Same accessible name, same destination, same `next`, one owner for
// the URL (session.js). That is a same-purpose duplicate, which is fine; what
// would not be fine is two spellings of the destination, which is why neither
// builds the string itself.
function PublicLanding() {
  useDocumentTitle(landing.title)
  const location = useLocation()
  const pages = publicPages()

  return (
    <article>
      <section className="d2d-hero">
        <div className="d2d-hero__wash" aria-hidden="true" />
        {/* The drift. Decorative, aria-hidden, pointer-events: none, and it
            lives inside the hero's own `overflow: hidden` so it cannot reach
            the page's scroll width. Under prefers-reduced-motion it stops
            entirely — measured, not assumed; see the note in app.css and the
            paired probe in src/test/viewport.test.js. */}
        <div className="d2d-hero__aurora" aria-hidden="true">
          <span className="d2d-hero__drift d2d-hero__drift--a" />
          <span className="d2d-hero__drift d2d-hero__drift--b" />
        </div>
        <div className="d2d-page d2d-hero__content">
          <h1 className="d2d-rise">
            <Lockup size="hero" />
          </h1>
        </div>
      </section>

      {/* Three children, and the DOM order is the PHONE order — which is also
          the reading order at every width. The canvas stacks lede, call, index,
          then the notice on a phone, and moves only the index into a second
          column on a wide screen; app.css does that with grid areas rather than
          by reordering anything, so what a screen reader hears never depends on
          the viewport. */}
      <div className="d2d-page">
        <div className="d2d-landing">
          <div className="d2d-landing__say">
            <p className="d2d-lede d2d-rise d2d-rise--delayed">{landing.intro}</p>

            <div className="d2d-landing__act">
              <a
                className="d2d-cta"
                href={signInHref(location)}
                data-testid="landing-signin"
                aria-label="Sign in with Discord"
              >
                {/* The sheen sweeps across the fill. Decorative in the strict
                    sense — it carries nothing, and app.css removes it outright
                    under reduced motion rather than freezing it, because a
                    static highlight band parked across a button reads as a
                    rendering fault. */}
                <span className="d2d-cta__sheen" aria-hidden="true" />
                <span className="d2d-cta__label">Sign in with Discord</span>
              </a>
            </div>
          </div>

          <nav className="d2d-index d2d-stagger" aria-label="Public pages">
            {pages.map((section) => {
              const count = indexCount(section)
              return (
                <Link key={section.id} to={section.path} className="d2d-index__row">
                  <span className="d2d-index__say">
                    <span className="d2d-index__title">{section.title}</span>
                    <span className="d2d-index__body">{section.summary}</span>
                  </span>
                  {count ? (
                    <span
                      className="d2d-index__count d2d-micro"
                      data-testid={`index-count-${section.id}`}
                    >
                      {count}
                    </span>
                  ) : null}
                </Link>
              )
            })}
          </nav>

          <p className="d2d-notice">
            <TierBadge tier={1} /> Trips, ratings and anyone&rsquo;s history live behind the
            sign-in. There&rsquo;s nothing to join here — the server isn&rsquo;t open, and
            there&rsquo;s no list to get on.
          </p>
        </div>
      </div>
    </article>
  )
}
